import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import styles from "./RemoveProjectPopup.module.scss";

interface RemoveProjectPopupProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: () => void;
  projectName: string;
}



const RemoveProjectPopup = ({
  open,
  handleClose,
  projectName,
  handleSubmit,
}: RemoveProjectPopupProps): JSX.Element => {
  const [confirmationText, setConfirmationText] = useState("");
  const [understood, setUnderstood] = useState(false);

  const expectedConfirmationText = `DELETE ${projectName}`.trim();

  useEffect(() => {
    if (open) {
      setConfirmationText("");
      setUnderstood(false);
    }
  }, [open]);

  const canDelete =
    confirmationText.trim() === expectedConfirmationText && understood;


const consequences = [
  "The project will be permanently removed.",
  "Access to the project will be revoked.",
  "Associated resources may be deleted.",
  "Deleted projects cannot be restored.",
];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="removeprojectpopup"
    >
      <DialogTitle className={styles.removeProjectPopup__title}>
        <WarningAmberOutlinedIcon color="error" />
        Permanently Delete Project
      </DialogTitle>
      <DialogContent>
        <Typography>
          You are about to permanently delete <strong>{projectName}</strong>.
        </Typography>
        <Typography>
          Project deletion is intended only for projects that are no longer needed.
        </Typography>
        <Typography>
          Once confirmed:
        </Typography>
        <List dense>
          {consequences.map((text) => (
            <ListItem
              key={text}
              disableGutters
            >
              <ListItemIcon>
                <ClearIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={text} />
            </ListItem>
          ))}
        </List>
        <Typography className={styles.removeProjectPopup__confirmationLabel}>
          To continue, type 
          <Chip
            label="DELETE"
            size="small"
            color="error"
            className={styles.removeProjectPopup__deleteChip}
          />
          followed by the project name
        </Typography>
        <TextField
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          fullWidth
          variant="standard"
          placeholder={expectedConfirmationText}
        />
        <FormControlLabel
          className={styles.removeProjectPopup__agreementCheckbox}
          control={
            <Checkbox
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              color="error"
            />
          }
          label="I understand that this action is permanent and cannot be undone."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!canDelete}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveProjectPopup;

